import React from 'react';
import { map, sumBy, toArray } from 'lodash';
import { Link } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Person } from '@mui/icons-material';
import Image from 'common/components/Image';
import ItrvlIcon2 from 'common/components/Icons/ItrvlIcon2';

const GUTTER = 18;
const TYPE = { CLIENTS: 'clients', ITINERARIES: 'itineraries' };

const linkSx = {
  display: 'block',
  color: 'stoneGrey.black80',
  textDecoration: 'none',
  px: `${GUTTER}px`,
  '&:nth-of-type(even)': { backgroundColor: 'stoneGrey.white60' },
  '@media (hover: hover)': {
    transition: 'background-color 200ms',
    '&:hover': { backgroundColor: 'stoneGrey.base' },
  },
};

const clientSx = {
  whiteSpace: 'nowrap',
  display: 'grid',
  placeItems: 'center',
  gridTemplateColumns: 'repeat(6,1fr)',
  py: '5px',
  '& > div': {
    width: '100%',
    gridColumn: 'span 4',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&:nth-of-type(2n)': {
      gridColumn: 'span 2',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
};

const itinerarySx = theme => ({
  py: '0.5rem',
  borderBottom: `1px solid ${theme.palette.stoneGrey.white60}`,
  whiteSpace: 'nowrap',
  display: 'grid',
  placeItems: 'center',
  grid: 'auto / repeat(6,1fr)',
  gap: '5px',
  '& > div': {
    gridColumn: 'span 2',
    width: '100%',
    '&:nth-of-type(4n)': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&:nth-of-type(4n+1)': {
      color: 'darkGreen.base',
      gridColumn: 'span 6',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&:nth-of-type(4n+3)': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
});

const headshotSx = {
  height: '1.625rem',
  width: '1.625rem',
  flexShrink: 0,
  '& > img': {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
  },
};

const ellipsisSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const iconSx = {
  display: 'inline-flex',
  mr: '0.2rem',
  color: 'darkGreen.base',
  position: 'relative',
  top: '2px',
};

const toAll = type => {
  switch (type) {
    case TYPE.CLIENTS:
      return '/clients';
    case TYPE.ITINERARIES:
      return '/trips-and-payments';
    default:
      return false;
  }
};

const limitPlus = (length, limit) => {
  if (length < limit) return length;
  return `${limit}+`;
};

const renderAgent = result => (
  <Stack direction="row" alignItems="center" spacing="5px">
    <Box sx={headshotSx}>{result.agentHeadshotUri ? <Image alt="headshot" src={result.agentHeadshotUri} /> : <Person />}</Box>
    <Box component="span" sx={ellipsisSx}>
      {result.agentFullName}
    </Box>
  </Stack>
);

const renderClientCompact = result => (
  <Stack spacing={0.5} sx={{ py: 1 }} role="listitem" aria-label={result.name}>
    <div>{result.name}</div>
    <div>{renderAgent(result)}</div>
  </Stack>
);

const renderClientGrid = result => (
  <Box sx={clientSx} role="listitem" aria-label={result.name}>
    <div>{result.name}</div>
    <div>{renderAgent(result)}</div>
  </Box>
);

const renderItineraryCompact = result => (
  <Stack
    spacing={0.5}
    sx={theme => ({ py: 1, borderBottom: `1px solid ${theme.palette.stoneGrey.white60}` })}
    role="listitem"
    aria-label={result.name}
  >
    <Box data-test-id="itinerary-name" sx={{ color: 'darkGreen.base', ...ellipsisSx }}>
      {result.name}
    </Box>
    <div>
      <Box component="span" sx={iconSx}>
        <ItrvlIcon2 name="fileInvoice" style={{ strokeWidth: 0.1 }} />
      </Box>
      #{result.quoteId}
    </div>
    <Box sx={ellipsisSx}>
      <Box component="span" sx={iconSx}>
        <ItrvlIcon2 name="user" style={{ strokeWidth: 0.1 }} />
      </Box>
      {result.clientName}
    </Box>
    <div>{renderAgent(result)}</div>
  </Stack>
);

const renderItineraryGrid = result => (
  <Box sx={itinerarySx} role="listitem" aria-label={result.name}>
    <Box data-test-id="itinerary-name">{result.name}</Box>
    <div>
      <Box component="span" sx={iconSx}>
        <ItrvlIcon2 name="fileInvoice" style={{ strokeWidth: 0.1 }} />
      </Box>
      #{result.quoteId}
    </div>
    <div>
      <Box component="span" sx={iconSx}>
        <ItrvlIcon2 name="user" style={{ strokeWidth: 0.1 }} />
      </Box>
      {result.clientName}
    </div>
    <div>{renderAgent(result)}</div>
  </Box>
);

export default function SearchResults({ results, filteredResults, onLinkClick, limit, compact }) {
  return (
    <>
      {!sumBy(toArray(results), 'length') && (
        <Stack sx={theme => ({ py: `${GUTTER}px`, border: `1px solid ${theme.palette.stoneGrey.base}`, mb: '-1px' })}>
          <Box sx={{ textAlign: 'center' }} data-test-id="no-search-results">
            No matches found.{' '}
            <a href="https://itrvl.helpjuice.com" target="_blank" rel="noopener noreferrer">
              Need some help?
            </a>
          </Box>
        </Stack>
      )}

      {map(filteredResults, (resultType, type) => (
        <Stack
          key={type}
          sx={theme => ({ py: `${GUTTER}px`, border: `1px solid ${theme.palette.stoneGrey.base}`, mb: '-1px' })}
          data-test-id={`${type}-search-results-container`}
        >
          <Box
            sx={theme => ({
              textTransform: 'capitalize',
              borderBottom: `2px solid ${theme.palette.stoneGrey.white60}`,
              fontSize: '1rem',
              lineHeight: '1.875rem',
              px: `${GUTTER}px`,
              pb: 1,
              mb: 1,
            })}
            role="heading"
            aria-level={2}
            aria-label={type}
          >
            {type} ({limitPlus(resultType.length, limit)})
          </Box>
          <Stack data-test-id={`${type}-search-results-list`}>
            {map(resultType.slice(0, limit), result => (
              <React.Fragment key={`${type}-${result.id}`}>
                {type === TYPE.CLIENTS && (
                  <Box component={Link} to={`/clients/${result.id}`} onClick={onLinkClick} sx={linkSx}>
                    {compact ? renderClientCompact(result) : renderClientGrid(result)}
                  </Box>
                )}
                {type === TYPE.ITINERARIES && (
                  <Box
                    component={Link}
                    to={`/itinerary/${result.id}`}
                    onClick={onLinkClick}
                    sx={linkSx}
                    data-test-id={`itinerary-${result.id}`}
                  >
                    {compact ? renderItineraryCompact(result) : renderItineraryGrid(result)}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Stack>
          {type === TYPE.CLIENTS && (
            <Box
              sx={{
                textTransform: 'uppercase',
                textAlign: 'right',
                fontSize: '0.75rem',
                mt: '10px',
                px: `${GUTTER}px`,
                letterSpacing: 0.2,
              }}
            >
              <Box component={Link} to={toAll(type)} onClick={onLinkClick} sx={{ ...linkSx, '@media (hover: hover)': {} }}>
                View all {type} <ArrowForwardIcon sx={{ fontSize: '1rem', verticalAlign: 'sub' }} />
              </Box>
            </Box>
          )}
        </Stack>
      ))}
    </>
  );
}
